import { TADateFilter } from 'store/model';
import { ResponderRole } from './types';

export type SessionSentimentLabel = 'negative' | 'neutral' | 'positive' | 'unknown';
export type QuickFilter = 'all' | 'unresolved' | 'negative' | 'escalated';

export interface SessionExplorerAgent {
  id: string | null;
  name: string | null;
  message_count: number | null;
  emitter_role?: ResponderRole | null;
}

export interface SessionExplorerCustomer {
  id: string | null;
  name: string | null;
}

export interface SessionExplorerSentiment {
  score: number | null;
  label: SessionSentimentLabel;
}

export interface SessionExplorerSession {
  session_id: string;
  start_datetime: string | null;
  duration_seconds: number | null;
  message_count: number | null;
  contact_reason: string | null;
  resolved: boolean | null;
  is_escalated: boolean;
  sentiment: SessionExplorerSentiment;
  agents: SessionExplorerAgent[];
  customer?: SessionExplorerCustomer | null;
  categories: Array<{ id: string; path: string }>;
  tags: string[];
}

export interface SessionExplorerPagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface SessionExplorerFacets {
  resolved: { true: number; false: number; unknown: number };
  sentiment: { positive: number; neutral: number; negative: number };
  escalated: { true: number; false: number };
}

export interface SessionExplorerAppliedFilters {
  resolved: boolean | null;
  sentiment: Array<'negative' | 'neutral' | 'positive'>;
  agent_ids: string[];
  category_ids: string[];
  tags: string[];
  is_escalated: boolean | null;
  search: string | null;
  page: number;
  per_page: number;
}

export interface SessionExplorerListResponse {
  data: {
    rows: SessionExplorerSession[];
    pagination: SessionExplorerPagination;
    facets: SessionExplorerFacets;
    applied_filters: SessionExplorerAppliedFilters;
  };
}

interface FilterBarValue {
  attributeGuid: string;
  attributeName: string;
  dimId?: number | null;
  dimName?: string | null;
  op1?: string | number | boolean | null;
  op2?: string | number | boolean | null;
  opType: string;
  source: string;
  subAttribute: string | null;
  values: Array<string | number | boolean>;
  tag: string;
  displayName: string;
}

interface SessionExplorerListFilters {
  date_interval: TADateFilter;
  filterBarValues?: { filterBarValues: FilterBarValue[] };
  search?: string;
  resolved?: boolean;
  sentiment?: Array<'negative' | 'neutral' | 'positive'>;
  category_ids?: string[];
  is_escalated?: boolean;
  page: number;
  per_page: number;
}

export interface SessionExplorerListPayload {
  filters: SessionExplorerListFilters;
  widget_id?: 0;
  source: 'chat';
  actionName: 'session-explorer-list';
  external: boolean;
  mockEndpoint?: 'session-explorer-list';
}

export function deduplicateSessions(rows: SessionExplorerSession[]): SessionExplorerSession[] {
  const sessionIds = new Set<string>();

  return rows.filter((session) => {
    if (sessionIds.has(session.session_id)) return false;
    sessionIds.add(session.session_id);
    return true;
  });
}

function getString(
  record: Record<string, unknown>,
  camelCase: string,
  pascalCase: string
): string | null {
  const value = record[camelCase] ?? record[pascalCase];
  return typeof value === 'string' ? value : null;
}

function getNullableString(value: unknown): string | null | undefined {
  return value === undefined || value === null || typeof value === 'string' ? value : undefined;
}

function getNullableNumber(value: unknown): number | null | undefined {
  return value === undefined || value === null || typeof value === 'number' ? value : undefined;
}

function getSqlParam(value: unknown): string | number | boolean | null | undefined {
  return value === undefined ||
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
    ? value
    : undefined;
}

function getFilterValues(value: unknown): Array<string | number | boolean> | null {
  if (!Array.isArray(value)) return null;
  return value.every(
    (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
  )
    ? value
    : null;
}

function getFilterBarValue(value: unknown): FilterBarValue | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  const attributeGuid = getString(candidate, 'attributeGuid', 'AttributeGuid');
  const attributeName = getString(candidate, 'attributeName', 'AttributeName');
  const opType = getString(candidate, 'opType', 'OpType');
  const source = getString(candidate, 'source', 'Source');
  const tag = getString(candidate, 'tag', 'Tag');
  const displayName = getString(candidate, 'displayName', 'DisplayName');
  const values = getFilterValues(candidate.values ?? candidate.Values);
  if (!attributeGuid || !attributeName || !opType || !source || !tag || !displayName || !values) {
    return null;
  }

  const dimId = getNullableNumber(candidate.dimId ?? candidate.DimId);
  const dimName = getNullableString(candidate.dimName ?? candidate.DimName);
  const rawOp1 = candidate.op1 ?? candidate.Op1;
  const rawOp2 = candidate.op2 ?? candidate.Op2;
  const op1 = getSqlParam(rawOp1);
  const op2 = getSqlParam(rawOp2);
  const subAttribute = getNullableString(candidate.subAttribute ?? candidate.SubAttribute) ?? null;
  if ((rawOp1 !== undefined && op1 === undefined) || (rawOp2 !== undefined && op2 === undefined)) {
    return null;
  }

  return {
    attributeGuid,
    attributeName,
    dimId,
    dimName,
    op1,
    op2,
    opType,
    source,
    subAttribute,
    values,
    tag,
    displayName,
  };
}

export function getHostFilterBarValues(): { filterBarValues: FilterBarValue[] } | undefined {
  const hostFilters = window.globalFiltersBarFilters?.FilterBarValues;
  if (!Array.isArray(hostFilters)) return undefined;

  const filterBarValues = hostFilters
    .map(getFilterBarValue)
    .filter((filter): filter is FilterBarValue => filter !== null);

  return filterBarValues.length ? { filterBarValues } : undefined;
}

export function buildSessionExplorerListPayload(
  dateFilter: TADateFilter,
  isExternal: boolean,
  page: number,
  perPage: number,
  search: string,
  quickFilter: QuickFilter,
  categoryId?: string
): SessionExplorerListPayload {
  const filters: SessionExplorerListFilters = {
    date_interval: dateFilter,
    page,
    per_page: perPage,
  };
  const filterBarValues = getHostFilterBarValues();
  if (filterBarValues) filters.filterBarValues = filterBarValues;
  if (search.trim()) filters.search = search.trim();
  if (categoryId) filters.category_ids = [categoryId];
  if (quickFilter === 'unresolved') filters.resolved = false;
  if (quickFilter === 'negative') filters.sentiment = ['negative'];
  if (quickFilter === 'escalated') filters.is_escalated = true;

  return {
    filters,
    source: 'chat',
    actionName: 'session-explorer-list',
    external: isExternal,
    ...(isExternal ? { widget_id: 0 } : {}),
    ...(!isExternal ? { mockEndpoint: 'session-explorer-list' } : {}),
  };
}

export function getQuickFilter(appliedFilters: SessionExplorerAppliedFilters): QuickFilter {
  if (appliedFilters.resolved === false) return 'unresolved';
  if (appliedFilters.sentiment.length === 1 && appliedFilters.sentiment[0] === 'negative') {
    return 'negative';
  }
  if (appliedFilters.is_escalated === true) return 'escalated';
  return 'all';
}

export function formatSessionTime(value: string | null, currentDate = new Date()): string {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const currentDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate()
  );
  const sessionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDifference = Math.round(
    (currentDay.getTime() - sessionDay.getTime()) / (24 * 60 * 60 * 1000)
  );

  if (dayDifference === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (dayDifference === 1) return 'Yesterday';
  if (date.getFullYear() === currentDate.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatSessionDateTime(value: string | null, timeZone?: string): string {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  });
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return 'Unknown duration';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
}
